import React, { useState, Component } from 'react';
import { XIcon, ClockIcon, PhoneIcon, AlertCircleIcon, SmileIcon, CopyIcon, MessageSquareIcon, CalendarIcon, PackageIcon, TargetIcon, ActivityIcon } from 'lucide-react';
interface Lead {
  id: number;
  name: string;
  initials?: string;
  email: string;
  phone: string;
  callDuration?: string;
  leadType: 'cold' | 'warm' | 'hot';
  status: string;
  dealerCode?: string;
  dealerName?: string;
  carModel?: string;
}
interface LeadDetailOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
}
// AI Icon Component
function AIIcon({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center ${className}`} data-id="element-1243">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-blue-600" data-id="element-1244">
        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" data-id="element-1245" />
      </svg>
    </div>;
}
// User Avatar Component
function UserAvatar({
  initials,
  className = ''
}: {
  initials: string;
  className?: string;
}) {
  return <div className={`w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center text-white text-sm font-bold ${className}`} data-id="element-1246">
      {initials}
    </div>;
}
export function LeadDetailOverlay({
  isOpen,
  onClose,
  lead
}: LeadDetailOverlayProps) {
  const [activeTab, setActiveTab] = useState<'details' | 'ai-analytics'>('details');
  const [leadStatus, setLeadStatus] = useState('buy-mahindra-car');
  const [buyStatus, setBuyStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  if (!isOpen || !lead) return null;
  const userInitials = lead.initials || lead.name.substring(0, 2).toUpperCase();
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40 transition-opacity duration-300" onClick={onClose} data-id="element-1247" />

      {/* Overlay Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-[480px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out flex flex-col" data-id="element-1248">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-1249">
          <div className="flex items-center gap-2" data-id="element-1250">
            <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center" data-id="element-1251">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" data-id="element-1252">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" data-id="element-1253" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-1254">
              Lead Details
            </h2>
          </div>
          <div className="flex items-center gap-3" data-id="element-1255">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2" data-id="element-1256">
              <ClockIcon size={16} data-id="element-1257" />
              ACTIVITY LOGS
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1258">
              <XIcon size={20} data-id="element-1259" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6" data-id="element-1260">
          <button onClick={() => setActiveTab('details')} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1261">
            DETAILS
          </button>
          <button onClick={() => setActiveTab('ai-analytics')} className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'ai-analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1262">
            AI ANALYTICS
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1263">
          {activeTab === 'details' && <div className="space-y-5" data-id="element-1264">
              {/* Status Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2 text-sm text-blue-700" data-id="element-1265">
                <PhoneIcon size={16} data-id="element-1266" />
                <span className="font-medium" data-id="element-1267">
                  Call routed to dealer and answered.
                </span>
              </div>

              {/* Name and Email */}
              <div className="grid grid-cols-2 gap-4" data-id="element-1268">
                <div data-id="element-1269">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-1270">
                    Name
                  </label>
                  <input type="text" value={lead.name} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-1271" />
                </div>
                <div data-id="element-1272">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-1273">
                    Email
                  </label>
                  <input type="email" value={lead.email} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-1274" />
                </div>
              </div>

              {/* Phone Number, Call Duration, Lead Type */}
              <div className="grid grid-cols-3 gap-4" data-id="element-1275">
                <div data-id="element-1276">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-1277">
                    Phone Number
                  </label>
                  <input type="tel" value={lead.phone} readOnly className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900" data-id="element-1278" />
                </div>
                <div data-id="element-1279">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-1280">
                    Call Duration
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg h-[38px]" data-id="element-1281">
                    <ClockIcon size={14} className="text-blue-600" data-id="element-1282" />
                    <span className="text-sm font-semibold text-blue-600" data-id="element-1283">
                      {lead.callDuration || '12:21 Sec'}
                    </span>
                  </div>
                </div>
                <div data-id="element-1284">
                  <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-1285">
                    Lead Type
                  </label>
                  <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg h-[38px]" data-id="element-1286">
                    <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1287">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" data-id="element-1288">
                        <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-1289" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-blue-600" data-id="element-1290">
                      Cold
                    </span>
                  </div>
                </div>
              </div>

              {/* Lead Status Radio Buttons */}
              <div data-id="element-1291">
                <label className="block text-xs font-medium text-gray-700 mb-3" data-id="element-1292">
                  Lead Status
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-1293">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1294">
                    <input type="radio" name="leadStatus" value="buy-mahindra-car" checked={leadStatus === 'buy-mahindra-car'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-1295" />
                    <span className="text-sm text-gray-700 font-medium" data-id="element-1296">
                      BUY A MAHINDRA CAR
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1297">
                    <input type="radio" name="leadStatus" value="service-mahindra-car" checked={leadStatus === 'service-mahindra-car'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-1298" />
                    <span className="text-sm text-gray-500" data-id="element-1299">
                      SERVICE MAHINDRA CAR
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1300">
                    <input type="radio" name="leadStatus" value="feedback" checked={leadStatus === 'feedback'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-1301" />
                    <span className="text-sm text-gray-500" data-id="element-1302">FEEDBACK</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-1303">
                    <input type="radio" name="leadStatus" value="non-sale" checked={leadStatus === 'non-sale'} onChange={e => setLeadStatus(e.target.value)} className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" data-id="element-1304" />
                    <span className="text-sm text-gray-500" data-id="element-1305">NON SALE</span>
                  </label>
                </div>
              </div>

              {/* Buy A Mahindra Car Dropdown */}
              <div data-id="element-1306">
                <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-1307">
                  Buy A Mahindra Car
                </label>
                <select value={buyStatus} onChange={e => setBuyStatus(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27currentColor%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_0.5rem_center] bg-no-repeat pr-10" data-id="element-1308">
                  <option value="" data-id="element-1309">Select Status</option>
                  <option value="interested" data-id="element-1310">Interested</option>
                  <option value="test-drive" data-id="element-1311">Test Drive Scheduled</option>
                  <option value="negotiating" data-id="element-1312">Negotiating</option>
                  <option value="closed" data-id="element-1313">Closed</option>
                </select>
              </div>

              {/* Notes */}
              <div data-id="element-1314">
                <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-1315">
                  Notes
                </label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Enter Details" rows={3} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none" data-id="element-1316" />
              </div>

              {/* Sync State Progress */}
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-1317">
                <label className="block text-sm font-semibold text-gray-700 mb-4" data-id="element-1318">
                  Sync State
                </label>
                <div className="space-y-3" data-id="element-1319">
                  <div className="flex items-center justify-between text-[11px] font-semibold" data-id="element-1320">
                    <span className="text-gray-600" data-id="element-1321">CREATED</span>
                    <span className="text-gray-600" data-id="element-1322">OPENED</span>
                    <span className="text-gray-600" data-id="element-1323">SAVED</span>
                    <span className="text-blue-600" data-id="element-1324">SYNCED</span>
                    <span className="text-gray-400" data-id="element-1325">ENQUIRY</span>
                  </div>
                  <div className="flex items-center" data-id="element-1326">
                    <div className="flex-1 relative" data-id="element-1327">
                      <div className="h-3 bg-blue-600 rounded-l-full" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)'
                  }} data-id="element-1328"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-1329">
                      <div className="h-3 bg-blue-600" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-1330"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-1331">
                      <div className="h-3 bg-blue-600" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-1332"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-1333">
                      <div className="h-3 bg-blue-800" style={{
                    clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)'
                  }} data-id="element-1334"></div>
                    </div>
                    <div className="flex-1 relative -ml-1" data-id="element-1335">
                      <div className="h-3 bg-gray-300 rounded-r-full" style={{
                    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 8px 50%)'
                  }} data-id="element-1336"></div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-amber-600" data-id="element-1337">
                    <AlertCircleIcon size={14} data-id="element-1338" />
                    <span data-id="element-1339">Syncing Failed on 15 Nov 2024</span>
                  </div>
                </div>
              </div>

              {/* Model Dropdown */}
              <div data-id="element-1340">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1341">
                  Model
                </label>
                <select value={selectedModel} onChange={e => setSelectedModel(e.target.value)} className="w-full px-3 py-3 bg-white border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 24 24%27 fill=%27none%27 stroke=%27%239ca3af%27 stroke-width=%272%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27%3e%3cpolyline points=%276 9 12 15 18 9%27%3e%3c/polyline%3e%3c/svg%3e')] bg-[length:20px] bg-[right_0.75rem_center] bg-no-repeat pr-10" data-id="element-1342">
                  <option value="" data-id="element-1343">Select model</option>
                  <option value="thar" data-id="element-1344">Thar</option>
                  <option value="thar-roxx" data-id="element-1345">Thar Roxx</option>
                  <option value="scorpio-n" data-id="element-1346">Scorpio-N</option>
                  <option value="xuv700" data-id="element-1347">XUV700</option>
                  <option value="xuv400" data-id="element-1348">XUV400</option>
                  <option value="bolero" data-id="element-1349">Bolero</option>
                  <option value="bolero-neo" data-id="element-1350">Bolero Neo</option>
                </select>
              </div>

              {/* Lead Created On */}
              <div data-id="element-1351">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1352">
                  Lead Created on
                </label>
                <input type="text" value="15 Nov 2024" readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-1353" />
              </div>

              {/* Dealer Code and Dealer Name */}
              <div className="grid grid-cols-2 gap-4" data-id="element-1354">
                <div data-id="element-1355">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1356">
                    Dealer Code
                  </label>
                  <input type="text" value={lead.dealerCode || 'HR407'} readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-1357" />
                </div>
                <div data-id="element-1358">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1359">
                    Dealer Name
                  </label>
                  <input type="text" value={lead.dealerName || 'Prem Kumar'} readOnly className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600" data-id="element-1360" />
                </div>
              </div>
            </div>}

          {activeTab === 'ai-analytics' && <div className="space-y-6" data-id="element-1361">
              {/* Call Data Section */}
              <div className="bg-gray-50 rounded-xl p-5" data-id="element-1362">
                <div className="flex items-center gap-2 mb-4" data-id="element-1363">
                  <ActivityIcon size={18} className="text-blue-600" data-id="element-1364" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-1365">
                    CALL DATA
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-5" data-id="element-1366">
                  <div className="flex items-center gap-3" data-id="element-1367">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1368">
                      <ClockIcon size={20} className="text-blue-600" data-id="element-1369" />
                    </div>
                    <div data-id="element-1370">
                      <p className="text-xs text-gray-500 mb-0.5" data-id="element-1371">Duration</p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1372">04:32</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1373">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-1374">
                      <SmileIcon size={20} className="text-blue-600" data-id="element-1375" />
                    </div>
                    <div data-id="element-1376">
                      <p className="text-xs text-gray-500 mb-0.5" data-id="element-1377">Sentiment</p>
                      <p className="text-lg font-bold text-green-500" data-id="element-1378">
                        POSITIVE
                      </p>
                    </div>
                  </div>
                </div>

                <div data-id="element-1379">
                  <div className="flex items-center justify-between mb-2" data-id="element-1380">
                    <span className="text-sm text-gray-600" data-id="element-1381">
                      Call Completion Status
                    </span>
                    <span className="text-sm font-bold text-green-500" data-id="element-1382">
                      100%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1383">
                    <div className="h-full bg-green-500 rounded-full" style={{
                  width: '100%'
                }} data-id="element-1384"></div>
                  </div>
                </div>
              </div>

              {/* Call Intent Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-1385">
                <div className="flex items-center gap-2 mb-4" data-id="element-1386">
                  <TargetIcon size={18} className="text-blue-600" data-id="element-1387" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-1388">
                    CALL INTENT
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6" data-id="element-1389">
                  <div data-id="element-1390">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-1391">Reason</p>
                    <span className="inline-block px-4 py-1.5 bg-orange-50 text-orange-600 border border-orange-200 rounded-full text-sm font-medium" data-id="element-1392">
                      Purchase Intent
                    </span>
                  </div>
                  <div data-id="element-1393">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-1394">Purpose</p>
                    <p className="text-sm text-gray-700" data-id="element-1395">
                      Inquiring about new SUV models and test drive
                      availability.
                    </p>
                  </div>
                </div>
              </div>

              {/* Product Interest Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-1396">
                <div className="flex items-center gap-2 mb-4" data-id="element-1397">
                  <PackageIcon size={18} className="text-blue-600" data-id="element-1398" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-1399">
                    PRODUCT INTEREST
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-4" data-id="element-1400">
                  <div data-id="element-1401">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-1402">Product</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-1403">
                      Tata Harrier XZA+
                    </p>
                  </div>
                  <div data-id="element-1404">
                    <p className="text-xs text-blue-500 mb-2" data-id="element-1405">Budget</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-1406">
                      ₹20L - ₹25L
                    </p>
                  </div>
                </div>

                <div data-id="element-1407">
                  <p className="text-xs text-blue-500 mb-2" data-id="element-1408">Preferences</p>
                  <div className="flex flex-wrap gap-2" data-id="element-1409">
                    {['Dark Edition', 'Automatic', 'Sunroof', 'Diesel'].map(pref => <span key={pref} className="px-3 py-1.5 bg-white border border-gray-300 rounded-full text-sm text-gray-700" data-id="element-1410">
                          {pref}
                        </span>)}
                  </div>
                </div>
              </div>

              {/* Timeline / Preferences Section */}
              <div className="border-b border-gray-200 pb-6" data-id="element-1411">
                <div className="flex items-center gap-2 mb-4" data-id="element-1412">
                  <CalendarIcon size={18} className="text-blue-600" data-id="element-1413" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-1414">
                    TIMELINE / PREFERENCES
                  </h3>
                </div>

                <div className="grid grid-cols-2 gap-x-6 gap-y-4" data-id="element-1415">
                  <div data-id="element-1416">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-1417">
                      Expected Purchase
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-1418">
                      Within 2 weeks
                    </p>
                  </div>
                  <div data-id="element-1419">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-1420">
                      Conversion Score
                    </p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-1421">High</p>
                  </div>
                  <div data-id="element-1422">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-1423">Contact Time</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-1424">
                      After 6:00 PM
                    </p>
                  </div>
                  <div data-id="element-1425">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-1426">Appointment</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-1427">
                      Scheduled: Sat, 06th Dec, 11:00 AM
                    </p>
                  </div>
                  <div data-id="element-1428">
                    <p className="text-xs text-blue-500 mb-1" data-id="element-1429">Channel</p>
                    <p className="text-sm font-medium text-gray-900" data-id="element-1430">
                      WhatsApp
                    </p>
                  </div>
                </div>
              </div>

              {/* Question / Conversation by Voicebot Agent */}
              <div data-id="element-1431">
                <div className="flex items-center gap-2 mb-4" data-id="element-1432">
                  <MessageSquareIcon size={18} className="text-blue-600" data-id="element-1433" />
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide" data-id="element-1434">
                    QUESTION / CONVERSATION BY VOICEBOT AGENT
                  </h3>
                </div>

                <div className="space-y-4" data-id="element-1435">
                  {/* Name Question */}
                  <div className="flex items-start gap-3" data-id="element-1436">
                    <AIIcon data-id="element-1437" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-1438">Name?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1439">
                    <UserAvatar initials={userInitials} data-id="element-1440" />
                    <div className="pt-2" data-id="element-1441">
                      <p className="text-sm text-gray-900" data-id="element-1442">{lead.name}.</p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-1443">
                        <CopyIcon size={14} data-id="element-1444" />
                      </button>
                    </div>
                  </div>

                  {/* Email Question */}
                  <div className="flex items-start gap-3" data-id="element-1445">
                    <AIIcon data-id="element-1446" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-1447">Email?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1448">
                    <UserAvatar initials={userInitials} data-id="element-1449" />
                    <div className="pt-2" data-id="element-1450">
                      <p className="text-sm text-gray-900" data-id="element-1451">{lead.email}</p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-1452">
                        <CopyIcon size={14} data-id="element-1453" />
                      </button>
                    </div>
                  </div>

                  {/* Model Interested Question */}
                  <div className="flex items-start gap-3" data-id="element-1454">
                    <AIIcon data-id="element-1455" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-1456">
                      Model Interested?
                    </p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1457">
                    <UserAvatar initials={userInitials} data-id="element-1458" />
                    <div className="pt-2" data-id="element-1459">
                      <p className="text-sm text-gray-900" data-id="element-1460">
                        {lead.carModel || 'Scorpio-N'}
                      </p>
                      <button className="text-gray-400 hover:text-gray-600 mt-1" data-id="element-1461">
                        <CopyIcon size={14} data-id="element-1462" />
                      </button>
                    </div>
                  </div>

                  {/* Test Drive Question */}
                  <div className="flex items-start gap-3" data-id="element-1463">
                    <AIIcon data-id="element-1464" />
                    <p className="text-sm text-gray-700 pt-2.5" data-id="element-1465">Test Drive?</p>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-1466">
                    <UserAvatar initials={userInitials} data-id="element-1467" />
                    <div className="pt-2" data-id="element-1468">
                      <p className="text-sm text-gray-500 italic" data-id="element-1469">
                        Not Answered...
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between bg-white" data-id="element-1470">
          <button className="px-6 py-2.5 text-sm font-semibold text-blue-600 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors" data-id="element-1471">
            SAVE LEAD
          </button>
          <button className="px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm" data-id="element-1472">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" data-id="element-1473">
              <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" data-id="element-1474" />
            </svg>
            SYNC & SAVE
          </button>
        </div>
      </div>
    </>;
}